import { ChangeDetectionStrategy, Component, inject, Input, signal } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, CardGroupComponent, InputLabelComponent, LoaderComponent, ToastService } from 'mbs-ui-kit';
import { StorageType } from 'mbs-ui-shared';
import { ErrorCode } from 'mbs-ui-shared@constants/errorCodes';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { e2eLocators } from '../credential-forms.constants';

import { CommonModule } from '@angular/common';
import { CreationMode } from '@components/storage-accounts/add-storage-account-wizard/add-storage-account-wizard.constants';
import { GoogleDriveCredentialForm } from '../credential-forms.model';

@UntilDestroy()
@Component({
  selector: 'mbs-credential-form-google-drive',
  templateUrl: './credential-form-google-drive.component.html',
  styleUrls: ['../credential-forms.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:message)': 'onWindowMessage($event)'
  },
  imports: [ButtonComponent, CardGroupComponent, CommonModule, I18NextPipe, InputLabelComponent, LoaderComponent]
})
export class CredentialFormGoogleDriveComponent {
  private storageAccountsService = inject(StorageAccountsService);
  private toastService = inject(ToastService);
  private i18N = inject(I18NextPipe);

  @Input() form: GoogleDriveCredentialForm;
  @Input() mode: CreationMode;

  public readonly StorageType = StorageType;
  public readonly creationMode = CreationMode;
  protected readonly e2eLocators = e2eLocators;

  public needRevokeAccess = signal(false);

  public loading = signal(false);

  public logIn(): void {
    window.open(
      `/AP/Google/OAuth?action=add-storage-account`,
      'Google OAuth 2.0',
      'width=500,height=500,toolbar=0,menubar=0,location=0,status=1,scrollbars=1,resizable=1,left=0,top=0'
    );
  }

  public onWindowMessage(event: MessageEvent) {
    const { msg, data: credential } = event.data;

    if (msg === 'add-storage-account') {
      this.loading.set(true);
      this.storageAccountsService
        .googleOauth(credential)
        .pipe(
          finalize(() => this.loading.set(false)),
          catchError((err) => {
            if (err.error.typeCode === ErrorCode.NeedToRevokeGoogleToken) {
              this.toastService.error(this.i18N.transform('storages:addStorageAccountWizard.inputCredentialsStep.revokeGoogleToken'));
              this.needRevokeAccess.set(true);
              return of({ email: null, refreshToken: null });
            }

            throw err;
          })
        )
        .subscribe((res) => {
          this.form.patchValue(res);
        });
    }
  }

  public revokeAccess() {
    this.needRevokeAccess.set(false);
    window.open('https://myaccount.google.com/connections', '_blank');
  }
}
